/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState } from "react";
import Stack from "@mui/material/Stack";
import Skeleton from "@mui/material/Skeleton";
import Slider from "@mui/material/Slider";
import { Typography } from "design_system/src";

import { ReactComponent as Calendar } from "design_system/src/static/icons/date.svg";
import { ReactComponent as Motorcycle } from "design_system/src/static/icons/motorcycle.svg";
import { ReactComponent as Cash } from "design_system/src/static/icons/money.svg";

import { UniqueSelectionFilter } from "components/uniqueSelectionFilter/UniqueSelectionFilter";
import useSkus from "hooks/useSkus";
import { formatPrice } from "helpers/formatPrice";
import { SelectedSkuFilters, SkuFilterOptions } from "models/inventory/helpers/skuFilters.helpers";
import CustomAccordion from "../../accordion/CustomAcccordion";

interface Props {
  onChange: (changes: Partial<SelectedSkuFilters>) => void;
  filterOptions: SkuFilterOptions;
  loading: boolean;
}

const SkuInputFilters: React.FC<Props> = ({ onChange, filterOptions, loading }) => {
  const {
    selectedFilters: { brand, model, year, cc, minPrice, maxPrice } = {},
  } = useSkus();

  const { brands, models, years, ccs, priceMin, priceMax } = filterOptions;

  const hasPriceRange = priceMin !== null && priceMax !== null && priceMax > priceMin;

  const [priceValue, setPriceValue] = useState<[number, number]>([
    priceMin ?? 0,
    priceMax ?? 0,
  ]);

  useEffect(() => {
    if (priceMin === null || priceMax === null) return;
    setPriceValue([minPrice ?? priceMin, maxPrice ?? priceMax]);
  }, [priceMin, priceMax, minPrice, maxPrice]);

  const handlePriceChange = (_event: Event, newValue: number | number[]) => {
    setPriceValue(newValue as [number, number]);
  };

  const handlePriceCommit = (
    _event: React.SyntheticEvent | Event,
    newValue: number | number[]
  ) => {
    const [min, max] = newValue as [number, number];
    onChange({ minPrice: min, maxPrice: max });
  };

  const brandOptions = useMemo(
    () => brands.map((b) => ({ label: b, value: b })),
    [brands]
  );

  const modelOptions = useMemo(
    () => models.map((m) => ({ label: m, value: m })),
    [models]
  );

  const yearOptions = useMemo(
    () => years.map((y) => ({ label: y.toString(), value: y })),
    [years]
  );

  const cylinderOptions = useMemo(
    () => ccs.map((c) => ({ label: `${c} CC`, value: c })),
    [ccs]
  );

  const acordionOptions = useMemo(
    () => [
      {
        title: "Marca",
        expanded: false,
        icon: (props: any) => <Motorcycle {...props} />,
        children: !brand
          ? <UniqueSelectionFilter
            name="brand"
            value={brand}
            options={brandOptions}
            handleSelectChange={(value: any) => onChange({ brand: value })}
            direction="vertical"
          />
          : null,
      },
      {
        title: "Modelo",
        expanded: false,
        icon: (props: any) => <Motorcycle {...props} />,
        children: !model
          ? <UniqueSelectionFilter
            name="model"
            value={model}
            options={modelOptions}
            handleSelectChange={(value: any) => onChange({ model: value })}
            direction="vertical"
          />
          : null,
      },
      {
        title: "Año",
        expanded: false,
        icon: (props: any) => <Calendar {...props} />,
        children: !year
          ? <UniqueSelectionFilter
            name="years"
            value={year}
            options={yearOptions}
            handleSelectChange={(value: any) => onChange({ year: parseInt(value, 10) })}
          />
          : null,
      },
      {
        title: "Cilindraje",
        expanded: false,
        icon: (props: any) => <Motorcycle {...props} />,
        children: !cc
          ? <UniqueSelectionFilter
            name="cylinder"
            value={cc}
            options={cylinderOptions}
            handleSelectChange={(value: any) => onChange({ cc: parseInt(value, 10) })}
          />
          : null,
      },
      {
        title: "Precio",
        expanded: false,
        icon: (props: any) => <Cash {...props} />,
        children: hasPriceRange
          ? (
            <div style={{ padding: "8px 16px" }}>
              <Slider
                value={priceValue}
                min={priceMin as number}
                max={priceMax as number}
                onChange={handlePriceChange}
                onChangeCommitted={handlePriceCommit}
                valueLabelDisplay="off"
                sx={{ color: "var(--primary_300, #35D17F)" }}
              />
              <div className="display_flex flex_justify_between">
                <Typography scale="xsmall" weight="400" textColor="neutral_600">
                  ${formatPrice(priceValue[0])}
                </Typography>
                <Typography scale="xsmall" weight="400" textColor="neutral_600">
                  ${formatPrice(priceValue[1])}
                </Typography>
              </div>
            </div>
          )
          : null,
      },
    ],
    [brand, model, year, cc, hasPriceRange, priceValue, priceMin, priceMax, brandOptions, modelOptions, yearOptions, cylinderOptions]
  );

  return loading ? (
    <div style={{ padding: 15 }} className="w_100_per">
      <Stack spacing={1}>
        <Skeleton variant="rounded" width="100%" height={50} />
        <Skeleton variant="rounded" width="100%" height={50} />
        <Skeleton variant="rounded" width="100%" height={50} />
        <Skeleton variant="rounded" width="100%" height={50} />
        <Skeleton variant="rounded" width="100%" height={50} />
      </Stack>
    </div>
  ) : (
    <CustomAccordion options={acordionOptions} />
  );
};

export default SkuInputFilters;
